<template>
  <div>
    <v-dialog v-model="open" v-if="members != null" scrollable persistent max-width="640px">
      <v-card class="c-edit-event">
        <!-- Header -->
        <v-card-title>
          <select-community-header
            ref="cid"
            v-model="data.cid"
            :title="talk ? $T('talk.setting.title') : $T('talk.create.title')"
            :fixCommunity="isEdit"
            svg="talk"
          ></select-community-header>
        </v-card-title>

        <v-divider></v-divider>
        <v-card-text>
          <select-groups-members-field
            ref="members"
            v-model="data.members"
            attribute="members"
            :cid="data.cid"
            :isEdit="isEdit"
            :creatorCmid="talk ? talk.cmid : cidToCmid(data.cid)"
            :excludeSelf="true"
            :inviteOnly="inviteOnly"
            :retiredCmids="retires"
            :defaultSelectedCmids="defaultSelectedCmids"
            @defaultValueSet="onDefaultValueSet"
          />
          <v-flex>
            <div class="c-flex c-cover-area">
              <div class="c-cover">
                <single-crop v-show="!personalTarget" v-model="data.cover" :defaultPath="defaultCover" />
                <div class="c-user-avator">
                  <cmid-avatar
                    v-if="!!personalTarget && cms"
                    :cmid="personalTargetCmid"
                    :cid="data.cid"
                    :cms="cms"
                    :size="80"
                  />
                </div>
              </div>
              <div class="c-name">
                <v-text-field
                  v-show="!personalTarget"
                  data-vv-name="name"
                  data-vv-validate-on="blur"
                  ref="name"
                  :label="$T('attributes.name')"
                  v-model="data.name"
                  color="clr-primary"
                  :counter="50"
                  v-validate="'required|max:50'"
                  :error-messages="getMessage('name')"
                  required
                  :disabled="loading"
                ></v-text-field>
                <div v-if="!!personalTarget && cms" class="c-person-name">
                  {{ getCmName(cms[personalTargetCmid], data.cid) }}
                </div>
              </div>
            </div>
          </v-flex>
          <v-flex v-if="!isEdit || talk.is_creator" class="c-check">
            <v-checkbox
              v-show="!personalTarget"
              color="clr-primary"
              :label="$T('talk.editable')"
              v-model="data.editable"
              hide-details
              :height="48"
              :disabled="loading"
            ></v-checkbox>
            <div v-show="!!personalTarget" class="c-personal-msg clr-form--text">{{ $T('talk.personal') }}</div>
          </v-flex>
        </v-card-text>
        <v-divider></v-divider>

        <!-- Footer -->
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="clr-btn" flat @click.native="onConfirmed" :loading="loading" :disabled="loading">{{ $T('button.save') }}</v-btn>
          <v-btn color="clr-btn-cancel" flat @click.native="$emit('close')" :disabled="loading">{{ $T('button.cancel') }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getMembers, createTalk, createPersonalTalk, editTalk } from '../../api/communityTalk';
import talkPlugin from '../../utils/plugin/talkPlugin';

export default {
  props: {
    talk: { type: Object, required: false },
  },
  mixins: [talkPlugin],
  data() {
    return {
      data: {
        cid: this.talk ? this.talk.cid : this.getRouteCid(),
        name: this.talk ? this.talk.name : null,
        members: [],
        cover: null,
        editable: this.talk ? this.talk.editable : true,
      },
      nameAutoFilled: false,
      nameDirty: !!this.talk,

      members: null,

      cms: null,
      gs: null,

      open: true,
      apiErrors: {},
      errMsg: null,
      loading: false,
    };
  },
  mounted() {
    var self = this;
    this.initData();
  },
  watch: {
    open(n, o) {
      if (!n) return this.$emit('close');
    },
    'data.cid'(n, o) {
      this.cms = null;
      var self = this;
      this.getAllGroupsAndMembers(n, function(data) {
        self.cms = data.cms;
        self.gs = data.gs;
      });
    },
    'data.name'(n, o) {
      if (this.nameAutoFilled) {
        this.nameAutoFilled = false;
        return;
      }
      this.nameDirty = !!n;
    },
    'data.members'(n, o) {
      this.adjustName(n);
    },
  },
  computed: {
    isEdit() {
      return !!this.talk;
    },
    inviteOnly() {
      return this.talk && !this.talk.is_creator;
    },
    defaultCover() {
      if (this.talk && this.talk.original_cover) return `/cover/t/${this.talk.id}/${this.talk.cover_changed_at}`;
      return this.data.cid ? `/cover/c/${this.data.cid}` : '/imgs/covers/community/sky.jpg';
    },
    defaultSelectedCmids() {
      return this.inviteOnly && this.members ? this.members.keys : [];
    },
    retires() {
      return this.members ? this.members.retires : [];
    },
    personalTargetCmid() {
      var k = this.personalTarget;
      return k ? Number(k.substring(1)) : 0;
    },
    personalTarget() {
      // personal talk can not edit.
      var ms = this.data.members;
      var cid = this.data.cid;
      if (this.isEdit || !cid || !ms) return null;
      var n = ms.length;
      var user = 'm' + this.cidToCmid(cid);
      if (n == 1) {
        var m0 = ms[0];
        return m0 != user && m0.startsWith('m') ? m0 : null;
      }
      if (n == 2) {
        var m0 = ms[0];
        var m1 = ms[1];
        if ((m0 == user || m1 == user) && m0 != m1 && m1.startsWith('m') && m1.startsWith('m')) {
          return m0 == user ? m1 : m0;
        }
      }
      return null;
    },
  },
  methods: {
    initData() {
      if (!this.talk) {
        this.members = [];
        this.data.members = [];
        return;
      }

      this.members = null;
      this.data.members = [];
      var self = this;
      getMembers(this.talk.id)
        .then(function(response) {
          var d = response.data ? response.data : [];
          self.members = d;
          self.data.members = self.inviteOnly || !d.keys ? [] : d.keys;
        })
        .catch(function(e) {
          console.error('failed to get data', e);
          self.errGetData();
        });
    },
    adjustName(ms) {
      if (this.nameDirty || !ms || ms.length == 0 || !this.cms || !this.gs) return;
      var a1 = false,
        a2 = false,
        a3 = false;
      var name = '';
      var memberOnly = true;
      var user = 'm' + this.cidToCmid(this.data.cid);
      var hasUser = false;
      for (var i = 0; i < ms.length; i++) {
        if ('A1' == ms[i]) a1 = true;
        else if ('A2' == ms[i]) a2 = true;
        else if ('A3' == ms[i]) a3 = true;
        if (name.length != 0) name += ' / ';
        if (ms[i].substring(0, 1) != 'm') memberOnly = false;
        if (ms[i] == user) hasUser = true;
        name += this.getSelectedCmName(ms[i]);
      }
      if (memberOnly && !hasUser && name) name = `${name} / ${this.getSelectedCmName(user)}`;
      if (a1 || (a2 && a3)) name = 'ALL ' + this.getCommunityName(this.data.cid);
      if (name.length > 47) name = name.substring(0, 47) + '...';

      this.nameAutoFilled = true;
      this.data.name = name;
      this.fields.name.invalid = false;
    },
    getSelectedCmName(key) {
      return this.getMemberKeyName(this.cms, this.gs, key, '');
    },
    initFocus() {
      if (this.isEdit) {
        this.$refs.name.focus();
      } else {
        if (this.isAllCommunitySelected) {
          this.$refs.cid.focus();
        } else {
          this.$refs.name.focus();
        }
      }
    },
    onConfirmed() {
      this.loading = true;

      if (this.personalTargetCmid) {
        this.registerPersonalTalk();
        return;
      }

      var valid = (this.talk || this.$refs.cid.isValid()) && this.$refs.members.isValid();
      var self = this;
      this.$validator.validateAll().then(result => {
        if (!valid || !result) return (self.loading = false);
        if (self.isEdit) {
          self.update();
        } else {
          self.register();
        }
      });
    },
    registerPersonalTalk() {
      var result = this.gotoPersonalTalk(this.cidToCmid(this.data.cid), this.personalTargetCmid);
      if (result) return;

      var self = this;
      var d = {
        cid: this.data.cid,
        members: this.data.members,
      };
      createPersonalTalk(setupFormData(d))
        .then(function(response) {
          self.mergeAndMoveToTalk(response.data);
          self.open = false;
          self.loading = false;
        })
        .catch(function(error) {
          console.error(error);
          self.loading = false;
          self.shortMsg($T('common.failed.register'), true);
        });
    },
    register() {
      var self = this;
      createTalk(setupFormData(this.data))
        .then(function(response) {
          self.mergeAndMoveToTalk(response.data);
          self.open = false;
          self.loading = false;
        })
        .catch(function(error) {
          console.error(error);
          self.loading = false;
          self.shortMsg($T('common.failed.register'), true);
        });
    },
    update() {
      var tid = this.talk.id;
      var data = setupFormData(this.data);
      data.append('tid', tid);
      var self = this;
      editTalk(data)
        .then(function(response) {
          self.mergeStoredTalks(response.data, tid);
          self.open = false;
          self.loading = false;
        })
        .catch(function(error) {
          console.error(error);
          self.loading = false;
          self.shortMsg($T('common.failed.edit'), true);
        });
    },
    onDefaultValueSet() {
      this.$refs.name.focus();
    },
    getMessage(fieldName) {
      return this.apiErrors[fieldName] && this.apiErrors[fieldName][this.inputValue]
        ? this.apiErrors[fieldName][this.inputValue]
        : this.errors.collect(fieldName);
    },
  },
};
</script>

<style scoped>
.input-group__details {
  margin: -10px 0 30px 0;
}
.c-dp {
  margin: -5px 0 10px 0;
}
.c-action-container {
  padding: 3px 20px 5px 20px;
}

.c-check {
  margin-top: -15px;
  min-height: 52px;
}

.c-cover-area {
  margin: 25px 0;
}
.c-cover {
  min-width: 120px;
  min-height: 82px;
  justify-content: center;
  align-items: center;
}
.le-tbs .c-cover-area {
  margin: 30px 0 0 0;
  flex-direction: column !important;
}
.le-tbs .c-img-item {
  left: 50%;
  transform: translateX(-50%);
  margin-top: -8px;
  margin-bottom: 8px;
}
.c-user-avator {
  width: 80px;
  margin: 1px auto;
}
.c-personal-msg {
  padding-top: 18px;
}
.c-name {
  padding-left: 20px;
  flex-grow: 1;
}
.c-person-name {
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 20px;
  min-height: 45px;
}
.le-tbs .c-person-name {
  justify-content: center;
}
.le-tbs .c-name {
  padding-left: 0px;
}
</style>
